//NewUserCreateOrganizationDialog
import { OrganizationService, WorkspaceService } from '@/api/';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { XMarkIcon } from '@heroicons/react/24/outline';

import NewUserCreateOrganizationDialog from '@/components/New-user-create-org-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import * as jose from 'jose';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

let showWelcomeMessageTimer: NodeJS.Timeout;
let showPromptTimer: NodeJS.Timeout;

export const HomePage = () => {
  const org = useStore($org);
  const OrgsStore = useStore($orgs);
  const user = useStore($user);
  const workspace = useStore($workspace);
  const WorkspacesStore = useStore($workspaces);
  const loadingProjects = useStore($loadingProjects);
  const [metabaseUrl, setMetabaseUrl] = useState<undefined | string>(undefined);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);

  const {
    data: orgs,
    isLoading: orgsLoading,
    isSuccess: isSuccessOrgsQuery,
  } = useQuery({
    queryKey: ['getOrganizations'],
    queryFn: OrganizationService.getOrganizations,
  });

  const {
    data: workspaces,
    isLoading: workspacesLoading,
    refetch: refetchWorkspaces,
    isSuccess: isSuccessWorkspaceQuery,
  } = useQuery({
    queryKey: ['getWorkspaces', org?.id],
    queryFn: () => WorkspaceService.getWorkspaces(org?.id || ''),
    enabled: !!org,
  });

  useEffect(() => {
    // Cleanup function for timers
    const cleanupTimers = () => {
      if (showWelcomeMessageTimer) {
        clearTimeout(showWelcomeMessageTimer);
      }
      if (showPromptTimer) {
        clearTimeout(showPromptTimer);
      }
    };

    // Run this effect only if there are no organizations yet and the dialog is not already open
    if (isSuccessOrgsQuery && OrgsStore.length === 0 && !isDialogOpen) {
      if (orgs.length > 0) {
        // Set the orgs
        $orgs.set(orgs);
        const firstOrg = orgs[0]; // Assuming orgs is an array of organizations
        $org.set(firstOrg);
      } else {
        // Show prompt after 5 seconds if no organizations are present
        showPromptTimer = setTimeout(() => {
          setShowPrompt(true);
        }, 5000); // 5 seconds
      }

      // Show welcome message after 2 seconds if the dialog is not already open
      showWelcomeMessageTimer = setTimeout(() => {
        setShowWelcomeMessage(true);
      }, 2000); // 2 seconds
    }

    // Cleanup function
    return cleanupTimers;
  }, [isSuccessOrgsQuery, orgs, OrgsStore.length, isDialogOpen]);

  const handleNextClick = () => {
    setShowWelcomeMessage(false); // Close the welcome message
    setDialogOpen(true); // Open the organization creation dialog
    setShowPrompt(false); // Ensure the prompt disappears if it's visible

    // Clear any existing timers for the welcome message and the prompt
    if (showWelcomeMessageTimer) {
      clearTimeout(showWelcomeMessageTimer);
    }
    if (showPromptTimer) {
      clearTimeout(showPromptTimer);
    }
  };

  const handleCreateOrgFromPrompt = () => {
    setDialogOpen(true);
    setShowPrompt(false);
    if (showWelcomeMessageTimer) {
      clearTimeout(showWelcomeMessageTimer);
    }
  };

  useEffect(() => {
    // run this effect only if there are no workspaces yet,
    if (isSuccessWorkspaceQuery && WorkspacesStore.length == 0) {
      if (workspaces.length > 0) {
        // set the workspaces for the curent org
        $workspaces.set(workspaces);
        const firstWorkspace = workspaces[0];
        $workspace.set(firstWorkspace);
      } else {
        $workspace.set(undefined);
      }
    }
  }, [isSuccessWorkspaceQuery, workspaces]);

  useEffect(() => {
    // if (!org?.id) {
    //   setMetabaseUrl(undefined);
    //   return;
    // }
    setMetabaseUrl(undefined);


    let resource = { dashboard: 19 }; // fallback default
    const params: Record<string, any> = {};

    // Aven override
    if (org?.id === 'TWd9Sr18r8F4') {
      resource.dashboard = 4;
    }

    // Coast override (workspace level)
    else if (org?.id === 'Djr6Rq68aomd') {
      const wsCfg = workspace?.workspace?.configurations?.metabase_embed_payload;
      resource.dashboard = wsCfg?.resource?.dashboard ?? 12;

      if (workspace?.projects?.length) {
        params.project_id = workspace.projects.map((p) => p.id);
      }
    }

    // All others use org.config
    else {
      const orgCfg = (org as any)?.configurations?.metabase_embed_payload;
      if (orgCfg?.resource?.dashboard) {
        resource.dashboard = orgCfg.resource.dashboard;
      }
      if (orgCfg?.params?.organization_id) {
        params.organization_id = orgCfg.params.organization_id;
      } else {
        params.organization_id = org?.id; // fallback to raw org.id
      }
    }

    const payload = {
      resource,
      params,
      exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
    };

    const secret = new TextEncoder().encode(import.meta.env.VITE_METABASE_SECRET);

    new jose.SignJWT(payload)
      .setProtectedHeader({ alg: 'HS256' })
      .setIssuedAt()
      .setExpirationTime('24h')
      .sign(secret)
      .then((token) => {
        const iframeUrl = `https://metabase.tennisfinance.com/embed/dashboard/${token}#titled=false`;
        setMetabaseUrl(iframeUrl);
      })
      .catch((err) => console.error('Metabase JWT signing failed', err));
  }, [org, workspace]);



  console.log('org', org);
  return (
    <div>
      {/* {org?.orgType === 'SAAS' ? ( */}
      {/* { (org?.orgType === 'SAAS' || org?.orgType === 'SMB') ? ( */}
      {org ? (
        <>
          {showWelcomeMessage && (
            <Dialog
              open={showWelcomeMessage}
              onOpenChange={() => setShowWelcomeMessage(false)}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Welcome to Tennis Finance!</DialogTitle>
                  <DialogDescription>
                    To get started, please create an organization. This will allow
                    you to manage projects and collaborators.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button onClick={handleNextClick}>Next</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <div className="flex flex-1 items-center">
            <h2 className="text-3xl font-semibold tracking-tight">Dashboard</h2>
            <h2 className="ml-1 text-2xl font-semibold tracking-tight text-muted-foreground">
              - Projects Overview
            </h2>
          </div>

          {orgsLoading || workspacesLoading || loadingProjects ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="mt-4 flex flex-1">
                {orgs && orgs?.length > 0 && (
                  <div>
                    <p className="mb-1 text-sm text-muted-foreground">
                      Organization
                    </p>
                    <Select
                      onValueChange={value => {
                        const foundOrg = orgs?.find(
                          (organ, o) => organ?.id === value,
                        );
                        $org.set(foundOrg);
                      }}
                      value={org?.id}
                      defaultValue={org?.id}
                    >
                      <SelectTrigger className="mr-4 w-[180px]">
                        <SelectValue placeholder="Select an org" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Organizations</SelectLabel>
                          {orgs &&
                            orgs?.map((orgg, o) => (
                              <SelectItem value={orgg?.id} key={o}>
                                {orgg?.name}
                              </SelectItem>
                            ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {workspaces && workspaces?.length > 0 && (
                  <div>
                    <p className="mb-1 text-sm text-muted-foreground">Workspace</p>
                    <Select
                      onValueChange={value => {
                        const foundWorkspace = workspaces?.find(
                          (workspc, w) => workspc?.workspace?.id === value,
                        );

                        $workspace.set(foundWorkspace);
                      }}
                      value={workspace?.workspace?.id}
                      defaultValue={workspace?.workspace?.id}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select a workspace" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Workspaces</SelectLabel>
                          {workspaces &&
                            workspaces?.map((work, w) => (
                              <SelectItem value={work?.workspace?.id} key={w}>
                                {work?.workspace?.name}
                              </SelectItem>
                            ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {metabaseUrl ? (
                <iframe
                  src={metabaseUrl}
                  width="100%"
                  height="1100"
                  title="Embedded Content"
                  id="metabase-content"
                  ref={iframeRef}
                  className="mt-6"
                />
              ) : !orgs || (orgs && orgs?.length < 1) ? (
                <div className="mt-2 flex flex-1 text-muted-foreground">
                  We could not find any organizations on your account. Please create
                  an organization{' '}
                  <p
                    className="ml-1 cursor-pointer underline underline-offset-1"
                    onClick={() => {
                      openPage($router, 'organizations');
                    }}
                  >
                    here
                  </p>
                </div>
              ) : orgs &&
                orgs?.length > 0 &&
                (!workspaces || (workspaces && workspaces?.length < 1)) ? (
                <div className="mt-2 flex flex-1 text-muted-foreground">
                  We could not find any workspaces for the selected organization.
                  Please create a workspace{' '}
                  <p
                    className="ml-1 cursor-pointer underline underline-offset-1"
                    onClick={() => {
                      openPage($router, 'workspaces', {
                        organizationId: org!.id,
                      });
                    }}
                  >
                    here
                  </p>
                </div>
              ) : (
                <div className="mt-6 flex flex-1 text-muted-foreground">
                  We could not find any projects data for the selected workspace.
                  Please{' '}
                  <p
                    className="ml-1 mr-1 cursor-pointer underline underline-offset-1"
                    onClick={() => {
                      openPage($router, 'projects', {
                        organizationId: org!.id,
                        workspaceId: workspace!.workspace.id,
                      });
                    }}
                  >
                    create
                  </p>{' '}
                  one or select another workspace.
                </div>
              )}
            </>
          )}
          {showPrompt && (
            <div className="fixed bottom-4 right-4 rounded-md border border-gray-200 bg-white p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  It looks like you don't have any organizations yet. Would you like
                  to create one?
                </p>
                <button
                  onClick={() => setShowPrompt(false)}
                  className={cn(
                    'ml-2 rounded-full p-1 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400',
                    'focus:ring-offset-2 focus:ring-offset-white',
                    'text-gray-500',
                  )}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <Button
                variant="outline"
                onClick={handleCreateOrgFromPrompt}
                className="mt-2"
              >
                Create Organization
              </Button>
            </div>
          )}
          <NewUserCreateOrganizationDialog
            open={isDialogOpen}
            setOpen={setDialogOpen}
          />
        </>
      ) : (
        <>
          {showWelcomeMessage && (
            <Dialog
              open={showWelcomeMessage}
              onOpenChange={() => setShowWelcomeMessage(false)}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Welcome to Tennis Finance!</DialogTitle>
                  <DialogDescription>
                    To get started, please create an organization. This will allow
                    you to manage projects and collaborators.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button onClick={handleNextClick}>Next</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <div className="flex flex-1 items-center">
            <h2 className="text-3xl font-semibold tracking-tight">Dashboard</h2>
            <h2 className="ml-1 text-2xl font-semibold tracking-tight text-muted-foreground">
              - Accounts Overview
            </h2>
          </div>
          {showPrompt && (
            <div className="fixed bottom-4 right-4 rounded-md border border-gray-200 bg-white p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  It looks like you don't have any organizations yet. Would you like
                  to create one?
                </p>
                <button
                  onClick={() => setShowPrompt(false)}
                  className={cn(
                    'ml-2 rounded-full p-1 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400',
                    'focus:ring-offset-2 focus:ring-offset-white',
                    'text-gray-500',
                  )}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <Button
                variant="outline"
                onClick={handleCreateOrgFromPrompt}
                className="mt-2"
              >
                Create Organization
              </Button>
            </div>
          )}
          <NewUserCreateOrganizationDialog
            open={isDialogOpen}
            setOpen={setDialogOpen}
          />          {showPrompt && (
            <div className="fixed bottom-4 right-4 rounded-md border border-gray-200 bg-white p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  It looks like you don't have any organizations yet. Would you like
                  to create one?
                </p>
                <button
                  onClick={() => setShowPrompt(false)}
                  className={cn(
                    'ml-2 rounded-full p-1 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400',
                    'focus:ring-offset-2 focus:ring-offset-white',
                    'text-gray-500',
                  )}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <Button
                variant="outline"
                onClick={handleCreateOrgFromPrompt}
                className="mt-2"
              >
                Create Organization
              </Button>
            </div>
          )}
          <NewUserCreateOrganizationDialog
            open={isDialogOpen}
            setOpen={setDialogOpen}
          />
        </>
      )}
    </div>
  );
};
